// Copyright © 2026 Alex Bruce. All rights reserved.
// Proprietary and confidential.

/**
 * A graphic scale — the only scale that survives printing.
 *
 * "1 inch = 20 feet" is a claim about paper. It holds if the sheet is printed
 * at exactly the size it was drawn for, and nothing guarantees that: the
 * export is sized from the browser window, every PDF reader offers "fit to
 * page", and a drawing that has been photocopied, emailed and printed again
 * has been rescaled two or three times on the way.
 *
 * A drawn bar has no such problem. It is part of the image, so whatever
 * happens to the sheet happens to the bar as well, and a ruler laid against
 * it reads true at any size. This is why every civil drawing carries one, and
 * why ours now does.
 */

/** The bar lengths worth offering, in feet. */
export const BAR_STEPS = [5, 10, 20, 25, 50, 100, 200, 250, 500, 1000, 2000];

export interface Bar {
  /** How many feet the bar spans. */
  feet: number;
  /** How long that is, in the drawing's own pixels. */
  px: number;
  /** How many divisions to draw. */
  divisions: number;
}

/**
 * The longest round number of feet that fits the room available.
 *
 * Round, because a scale bar is read by eye: 50 feet is a measurement anyone
 * can divide in their head, and 47 is not.
 */
export function niceBar(feetPerPixel: number, maxPx: number): Bar | null {
  if (!feetPerPixel || !Number.isFinite(feetPerPixel) || feetPerPixel <= 0) return null;
  if (!maxPx || !Number.isFinite(maxPx) || maxPx <= 0) return null;

  let chosen = BAR_STEPS[0];
  for (const feet of BAR_STEPS) {
    if (feet / feetPerPixel <= maxPx) chosen = feet;
    else break;
  }
  const px = chosen / feetPerPixel;
  // Even the shortest step can be too long on a very small sheet; a bar that
  // runs off the edge is worse than no bar, so there is none.
  if (px > maxPx) return null;

  // Four divisions for a round number, two when halving it would give an
  // awkward tick (25 and 250 divide into quarters; 20 into four fives).
  const divisions = chosen % 4 === 0 || chosen % 100 === 0 || chosen % 25 === 0 ? 4 : 2;
  return { feet: chosen, px, divisions };
}

/** The label at the far end of the bar. */
export function barLabel(bar: Bar | null): string {
  if (!bar) return "";
  return `${bar.feet} FT`;
}

/**
 * The note that makes the bar mean something.
 *
 * Without it a reader has no way to tell a sheet printed true from one a
 * printer has shrunk by 6% to fit its margins, and both look equally
 * official. With it, one measurement settles the question.
 */
export function verifyNote(bar: Bar | null): string {
  if (!bar) return "";
  return `IF THIS BAR IS NOT ${bar.feet} FT AT THE STATED SCALE, THE SHEET HAS BEEN RESIZED`;
}

/**
 * How many fine ticks the first division is cut into.
 *
 * A surveyor's bar is not evenly striped end to end: the leftmost division is
 * subdivided, so a distance shorter than one division can still be read off
 * it. The cut has to land on a round number of feet — five ticks across a
 * 25' division is 5' each, and four would be 6'3".
 */
export function subdivisions(cellFeet: number): number {
  if (!Number.isFinite(cellFeet) || cellFeet <= 0) return 1;
  for (const n of [5, 4, 2]) {
    const step = cellFeet / n;
    if (Number.isInteger(step) && step >= 1) return n;
  }
  return 1;
}

/** One block of the bar, in pixels from its start. */
export interface BarCell { x0: number; x1: number }

/**
 * The blocks of the bar, left to right.
 *
 * The first division comes first, cut into fine ticks, then the whole
 * divisions. Drawn as two rows with the fill alternating along this list and
 * the lower row inverted, which is the chequer every drawing office uses and
 * what makes a bar readable at a glance rather than a striped rectangle.
 */
export function barCells(bar: Bar | null): BarCell[] {
  if (!bar || bar.divisions <= 0) return [];
  const cellPx = bar.px / bar.divisions;
  const fine = subdivisions(bar.feet / bar.divisions);
  const out: BarCell[] = [];
  for (let i = 0; i < fine; i++) {
    out.push({ x0: (cellPx * i) / fine, x1: (cellPx * (i + 1)) / fine });
  }
  for (let i = 1; i < bar.divisions; i++) out.push({ x0: cellPx * i, x1: cellPx * (i + 1) });
  return out;
}

/** A number printed under the bar, and where it goes. */
export interface BarTick { px: number; feet: number }

/**
 * The numbers under the bar: zero and each whole division.
 *
 * The fine ticks are deliberately left unlabelled. They are there to be
 * counted, and five more numbers crowded into the first inch of the bar is
 * how a scale becomes unreadable at the size it is actually printed.
 */
export function barTicks(bar: Bar | null): BarTick[] {
  if (!bar || bar.divisions <= 0) return [];
  const out: BarTick[] = [];
  for (let i = 0; i <= bar.divisions; i++) {
    out.push({ px: (bar.px * i) / bar.divisions, feet: (bar.feet * i) / bar.divisions });
  }
  return out;
}

/** Where each division line falls along the bar, in pixels from its start. */
export function divisionOffsets(bar: Bar | null): number[] {
  if (!bar) return [];
  const out: number[] = [];
  for (let i = 0; i <= bar.divisions; i++) out.push((bar.px * i) / bar.divisions);
  return out;
}
